import Panel from "../panel/Panel";
import "./payment-panel.css";
import {ReactNode, useEffect, useState} from "react";
import {startPricingPlanChargingProcess} from "../../api/startPricingPlanChargingProcess";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import {ToastSkin, ToastType} from "../../context/ToastContext";
import {CreditCardIcon} from "../../assets/icons/CreditCardIcon";
import {CodepenCircleOutlined, RightOutlined} from "@ant-design/icons";
import {Button} from "antd";
import {RefreshIcon} from "../../assets/icons/RefreshIcon";
import Loader from "../loader/Loader";
import useAppContext from "../../hooks/useAppContext";
import {EditIcon} from "../../assets/icons/EditIcon";
import InvoiceDetailsEditor from "./InvoiceDetailsEditor";

interface PaymentPanelNextProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    description?: string | ReactNode | ReactNode[];
    amountUsd: number;
    entityId?: number;
    paymentCall: (selectedDomain: string, entityId: number, isDirect?: boolean) => Promise<{ data?: { url?: string; cid?: boolean; m?: string; ld?: string; isSuccessful?: boolean; successDetails: any }, error?: string }>;
    // cid - has customer id boolean
    // m - credit card mask
    // ld - credit card last digits
    // onInitiateDirectCharge: (selectedDomain: string, entityId: number) => Promise<{ isSuccessful: boolean, successDetails: any }>;
    afterSuccess: (successDetails?: any) => void;
}

const PaymentPanelNext = ({ isOpen, onClose, entityId, title, description, amountUsd, paymentCall, afterSuccess }: PaymentPanelNextProps) => {
    const defaultURL = '';
    const defaultHasCustomer = false;
    const defaultMask = '';
    const defaultLastDigits = '';

    const { selectedDomain } = useUserContext();

    const { showToast, closeToast } = useToast();
    const [url, setUrl] = useState(defaultURL);
    const [hasCustomer, setHasCustomer] = useState(defaultHasCustomer);
    const [mask, setMask] = useState(defaultMask);
    const [lastDigits, setLastDigits] = useState(defaultLastDigits);
    const [changePaymentMethod, setChangePaymentMethod] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCharging, setIsCharging] = useState(false);
    const [paymentReloader, setPaymentReloader] = useState(0);

    const initiateValues = () => {
        setUrl(defaultURL);
        setHasCustomer(defaultHasCustomer);
        setMask(defaultMask);
        setLastDigits(defaultLastDigits);
        setChangePaymentMethod(false);
        setIsCharging(false);
        setIsLoading(false);
    }

    const loadPaymentOptions = async () => {
        setIsLoading(true);
        if(selectedDomain && entityId) {
            const response = selectedDomain ? await paymentCall(selectedDomain, entityId) : null;
            if(response?.data) {
                if(response?.data?.isSuccessful) {
                    afterSuccess(response?.data);
                } else if(!response.data.url && !response.data.cid && !response?.data?.isSuccessful) {
                    showToast({ skin: ToastSkin.error, text: 'We failed to load a payment screen. Please try again or contact support.'});
                    onClose();
                } else {
                    const formattedMask = response.data.m?.replace(/(.{4})/g, "$1 ").trim();
                    setUrl(response.data.url || '');
                    setHasCustomer(!!response.data.cid);
                    setMask(formattedMask || '');
                    setLastDigits(response.data.ld || '');
                }
            }
        }
        setIsLoading(false);
    }

    const initiateDirectCharge = async () => {
        if(isCharging || !entityId || !selectedDomain) return;
        setIsCharging(true);
        const response = await paymentCall(selectedDomain, entityId, true);
        if(response?.data?.isSuccessful) {
            afterSuccess(response?.data);
            onClose();
        }
        setIsCharging(false);
    }

    const onConfirm = (event: any) => {
        afterSuccess(event?.data);
        onClose();
    }

    useEffect(() => {
        if(isOpen && selectedDomain && entityId) {
            loadPaymentOptions();
        } else {
            initiateValues();
        }
    }, [isOpen, selectedDomain, entityId, paymentReloader]);

    useEffect(() => {
        const ch = new BroadcastChannel('vuelve-billing');
        ch.addEventListener("message", onConfirm);
        return () => {
            ch.removeEventListener("message", onConfirm);
        }
    }, [selectedDomain]);

    return <Panel isOpen={isOpen} onClose={onClose} darkenScreen={true} className="payment-panel">
        {isLoading ? <div className="loader-container"><Loader size={124} /></div> : hasCustomer && !changePaymentMethod ?
            <div className="existing-payment-method-container">
                <div className="title">{title}</div>
                {description || <div className="price">${Math.round(amountUsd * 1000) / 1000}</div>}
                <div className={`payment-section existing-payment-method-section ${isCharging ? 'disabled' : ''}`} onClick={initiateDirectCharge}>
                    <div className="title">Use Saved Payment Method</div>
                    <div className="description">Select your previously saved card to pay quickly and securely.</div>
                    <div className="existing-payment-method">
                        <div className="left">
                            <CreditCardIcon width={32} color={isCharging ? '#b9b9b9' : '#fff'} />
                            <div className="mask">{mask}</div>
                        </div>
                        <div className="right">
                            {isCharging ? <Loader size={24} /> : <RightOutlined />}
                        </div>
                    </div>
                </div>
                <div className={`payment-section different-payment-container ${isCharging ? 'disabled' : ''}`} onClick={() => {
                    if(isCharging) return;
                    setChangePaymentMethod(true);
                }}>
                    <div className="title">Change Payment Method</div>
                    <div className="description">Want to use a different card? Click below and enter the new card details.</div>
                    <div className="different-payment">
                        <Button type="text" disabled={isCharging} icon={<RefreshIcon width={14} color="#1575fb" />}>Change your payment method</Button>
                    </div>
                </div>
            </div>
        : hasCustomer && changePaymentMethod ?
            <div className="new-payment-method-with-old-option">
                <div className="use-existing-payment" onClick={() => setChangePaymentMethod(false)}>
                    <Button type="text">Use your previous payment method</Button>
                </div>
                <InvoiceDetailsEditor onUpdate={() => setPaymentReloader(paymentReloader + 1)} />
                <iframe src={url} width="100%" height="calc(100% - 140px - 48px - 48px)" />
            </div> : <>
                <div className="payment-panel-header" />
                <InvoiceDetailsEditor onUpdate={() => setPaymentReloader(paymentReloader + 1)} />
                <iframe src={url} width="100%" height="calc(100% - 140px - 48px - 48px)" />
            </>
        }
    </Panel>
}

export default PaymentPanelNext;